import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const ReportsPage = ({ type = 'sales' }) => {
  const { company, selectedFirmId, firms } = useContext(AuthContext);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedCustomer, setSelectedCustomer] = useState('0');
  const [selectedVendor, setSelectedVendor] = useState('0');
  const [selectedEmployee, setSelectedEmployee] = useState('0');

  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [reportData, setReportData] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const selectedFirm = firms.find((f) => String(f.Id) === String(selectedFirmId));

  useEffect(() => {
    loadFilterMasters();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let endpoint = '';
      if (type === 'sales') {
        endpoint = `/reports/sales?startDate=${startDate}&endDate=${endDate}&customerId=${selectedCustomer}&firmId=${selectedFirmId}`;
      } else if (type === 'purchases') {
        endpoint = `/reports/purchases?startDate=${startDate}&endDate=${endDate}&vendorId=${selectedVendor}&firmId=${selectedFirmId}`;
      } else if (type === 'employees') {
        endpoint = `/reports/employees?startDate=${startDate}&endDate=${endDate}&employeeId=${selectedEmployee}&firmId=${selectedFirmId}`;
      } else if (type === 'accounts') {
        endpoint = `/reports/accounts?startDate=${startDate}&endDate=${endDate}&firmId=${selectedFirmId}`;
      } else if (type === 'stock') {
        endpoint = '/reports/stock';
      } else if (type === 'customer-ledger') {
        endpoint = `/reports/customer-ledger?startDate=${startDate}&endDate=${endDate}&customerId=${selectedCustomer}&firmId=${selectedFirmId}`;
      } else if (type === 'vendor-ledger') {
        endpoint = `/reports/vendor-ledger?startDate=${startDate}&endDate=${endDate}&vendorId=${selectedVendor}&firmId=${selectedFirmId}`;
      }

      const res = await api.get(endpoint);
      if (type === 'customer-ledger' || type === 'vendor-ledger') {
        setLedgerSummary(res.data);
        setReportData([]);
        setSummary(null);
      } else if (Array.isArray(res.data)) {
        setReportData(res.data);
        setSummary(null);
        setLedgerSummary(null);
      } else {
        setReportData(res.data.data || []);
        setSummary(res.data);
        setLedgerSummary(null);
      }
    } catch (err) {
      setError('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [type, startDate, endDate, selectedFirmId, selectedCustomer, selectedVendor, selectedEmployee]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const loadFilterMasters = async () => {
    try {
      const [cRes, vRes, eRes] = await Promise.all([
        api.get('/masters/customers'),
        api.get('/masters/vendors'),
        api.get('/masters/employees')
      ]);
      setCustomers(cRes.data);
      setVendors(vRes.data);
      setEmployees(eRes.data);
    } catch (err) {
      console.error('Error loading report filter masters', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleWhatsAppShare = () => {
    if (!reportData) {
      alert('पाठवण्यासाठी कोणताही रिपोर्ट डेटा उपलब्ध नाही (No report data to share)');
      return;
    }

    const companyName = selectedFirm?.Comapny_Name || selectedFirm?.Company_Name || selectedFirm?.firmName || 'Fish Syndicate';
    let targetMobile = '';
    let reportTitle = '';
    let summaryText = '';

    if (type === 'sales') {
      reportTitle = 'व्यापारी बील रिपोर्ट (Sales Report)';
      const totalAmount = Array.isArray(reportData) ? reportData.reduce((sum, r) => sum + Number(r.GrandTotal || 0), 0) : 0;
      summaryText = `📊 *${companyName}*\n\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\n🔢 एकूण बील संख्या: ${Array.isArray(reportData) ? reportData.length : 0}\n💰 एकूण बील रक्कम: ₹${totalAmount.toLocaleString('en-IN')}\n\nधन्यवाद!`;
    } else if (type === 'purchases') {
      reportTitle = 'खरेदी बील रिपोर्ट (Purchase Report)';
      const totalAmount = Array.isArray(reportData) ? reportData.reduce((sum, r) => sum + Number(r.GranadTotal || r.GrandTotal || 0), 0) : 0;
      summaryText = `📊 *${companyName}*\n\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\n🔢 एकूण बील संख्या: ${Array.isArray(reportData) ? reportData.length : 0}\n💰 एकूण खरेदी रक्कम: ₹${totalAmount.toLocaleString('en-IN')}\n\nधन्यवाद!`;
    } else if (type === 'customer-ledger') {
      targetMobile = activeCustomer?.MobileNo || activeCustomer?.mobileNo || '';
      reportTitle = `व्यापारी खाते उतारा - ${activeCustomer?.Name || 'All'}`;
      const bills = reportData.sales || [];
      const payments = reportData.receipts || [];
      const opBal = Number(reportData.openingBalance || 0);
      const totalBill = bills.reduce((sum, b) => sum + Number(b.GrandTotal || b.total || 0), 0);
      const totalPaid = payments.reduce((sum, p) => sum + Number(p.PaidAmt || p.totalAmt || 0), 0);
      const closingBal = opBal + totalBill - totalPaid;
      summaryText = `📊 *${companyName}*\n\n👤 *व्यापारी नांव:* ${activeCustomer?.Name || 'All'}\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\n▶️ *आरंभीची बाकी (Opening Balance): ₹${opBal.toLocaleString('en-IN')}*\n🧾 कालावधीतील बील: ₹${totalBill.toLocaleString('en-IN')}\n💵 जमा झालेली रक्कम: ₹${totalPaid.toLocaleString('en-IN')}\n----------------------------------------\n⚠️ *अंतिम बाकी (Net Closing Balance): ₹${closingBal.toLocaleString('en-IN')}*\n\nकृपया उर्वरित रक्कम जमा करावी. धन्यवाद!`;
    } else if (type === 'vendor-ledger') {
      targetMobile = activeVendor?.MobileNo || activeVendor?.mobileNo || '';
      reportTitle = `विक्रेता खाते उतारा - ${activeVendor?.Name || 'All'}`;
      const bills = reportData.purchases || [];
      const payments = reportData.vouchers || [];
      const opBal = Number(reportData.openingBalance || 0);
      const totalBill = bills.reduce((sum, b) => sum + Number(b.GranadTotal || b.GrandTotal || b.total || 0), 0);
      const totalPaid = payments.reduce((sum, p) => sum + Number(p.PaidAmt || p.totalAmt || 0), 0);
      const closingBal = opBal + totalBill - totalPaid;
      summaryText = `📊 *${companyName}*\n\n🤝 *विक्रेता नांव:* ${activeVendor?.Name || 'All'}\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\n▶️ *आरंभीची बाकी (Opening Balance): ₹${opBal.toLocaleString('en-IN')}*\n🧾 कालावधीतील खरेदी: ₹${totalBill.toLocaleString('en-IN')}\n💵 दिलेली रक्कम: ₹${totalPaid.toLocaleString('en-IN')}\n----------------------------------------\n⚠️ *अंतिम बाकी (Net Closing Balance): ₹${closingBal.toLocaleString('en-IN')}*\n\nधन्यवाद!`;
    } else if (type === 'accounts') {
      reportTitle = 'पलट व खाते हिशोब रिपोर्ट (Boat Trip Report)';
      const trips = reportData.trips || [];
      const totalSale = trips.reduce((sum, t) => sum + Number(t.total_sale || 0), 0);
      summaryText = `📊 *${companyName}*\n\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\n🚢 एकूण फेऱ्या (Trips): ${trips.length}\n💰 एकूण धंदा/विक्री: ₹${totalSale.toLocaleString('en-IN')}\n\nधन्यवाद!`;
    } else {
      reportTitle = 'हिशोब रिपोर्ट (Account Report)';
      summaryText = `📊 *${companyName}*\n\n📋 *${reportTitle}*\n🗓️ कालावधी: ${startDate} ते ${endDate}\n\nहिशोब तपशील पाहण्यासाठी सॉफ्टवेअरमध्ये लॉगिन करा. धन्यवाद!`;
    }

    let cleanMobile = targetMobile.replace(/\D/g, '');
    if (cleanMobile.length === 10) {
      cleanMobile = '91' + cleanMobile;
    }

    const userPhone = window.prompt(
      'व्हॉट्सॲप नंबर टाका (Enter WhatsApp Mobile Number with Country Code, e.g. 919881914831):',
      cleanMobile || '91'
    );

    if (userPhone === null) return;

    const finalPhone = userPhone.replace(/\D/g, '');
    const encodedText = encodeURIComponent(summaryText);

    let whatsappUrl = '';
    if (finalPhone && finalPhone.length >= 10) {
      whatsappUrl = `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodedText}`;
    } else {
      whatsappUrl = `https://api.whatsapp.com/send?text=${encodedText}`;
    }

    window.open(whatsappUrl, '_blank');
  };

  const toggleRowDetail = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const activeCustomer = customers.find(c => String(c.Id) === String(selectedCustomer));
  const activeVendor = vendors.find(v => String(v.Id) === String(selectedVendor));

  return (
    <div className="app-container container-fluid py-3 px-2 px-md-4">
      {/* PRINT-ONLY JASPER REPORT HEADER BANNER */}
      <div className="d-none d-print-block text-center mb-3 pb-2 border-bottom border-dark border-3">
        <h2 className="fw-bold text-uppercase mb-0">{company?.Comapny_Name || company?.Company_Name || 'Hari Ganga Fish Syndicate'}</h2>
        <p className="mb-1 text-muted small">{company?.Comapny_Address || company?.Address || ''}</p>
        <p className="mb-1 text-muted small">{company?.Company_Mobile || company?.MobileNo ? `मोबाईल / संपर्कासाठी: ${company.Company_Mobile || company.MobileNo}` : ''}</p>
        {selectedFirm && (
          <p className="mb-0 fw-bold text-dark small">फर्म (Firm): {selectedFirm.firmName}</p>
        )}
        <div className="py-2 bg-light border-top border-bottom border-secondary border-2 mt-2">
          <h4 className="fw-bold text-uppercase mb-0 text-dark">
            {type === 'sales' && 'व्यापारी बील रिपोर्ट / SALES REPORT'}
            {type === 'purchases' && 'विक्रेता खरेदी रिपोर्ट / PURCHASE REPORT'}
            {type === 'employees' && 'खलाशी हिशोब रिपोर्ट / EMPLOYEE LEDGER REPORT'}
            {type === 'accounts' && 'पलट व खाते रिपोर्ट / BOAT TRIP & ACCOUNT REPORT'}
            {type === 'stock' && 'मासा स्टॉक रिपोर्ट / PRODUCT STOCK REPORT'}
            {type === 'customer-ledger' && `CUSTOMER WISE ACCOUNT REPORT (व्यापारी: ${activeCustomer?.Name || 'All'})`}
            {type === 'vendor-ledger' && `VENDOR WISE ACCOUNT REPORT (विक्रेता: ${activeVendor?.Name || 'All'})`}
          </h4>
          <span className="small text-dark fw-semibold">
            कालावधी (Period): {startDate} ते (To) {endDate} | प्रिंट तारीख: {new Date().toLocaleDateString('en-IN')}
          </span>
        </div>
      </div>

      {/* WEB DASHBOARD HEADER & FILTERS */}
      <div className="d-print-none d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 className="fw-extrabold mb-1 text-primary text-capitalize fs-4" style={{ fontFamily: 'var(--app-font-heading)' }}>
            <i className="bi bi-journal-check me-2"></i>
            {type === 'sales' && 'व्यापारी बील / Sales Report'}
            {type === 'purchases' && 'विक्रेता खरेदी / Purchase Report'}
            {type === 'employees' && 'खलाशी हिशोब / Employee Report'}
            {type === 'accounts' && 'पलट व खाते / Boat Trip Report'}
            {type === 'stock' && 'मासा स्टॉक / Stock Report'}
            {type === 'customer-ledger' && 'व्यापारी खाते उतारा / Customer Wise Account Report'}
            {type === 'vendor-ledger' && 'विक्रेता खाते उतारा / Vendor Wise Account Report'}
          </h3>
          <p className="text-muted mb-0 small">Jasper Report format with WhatsApp sharing and PDF print export</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-success btn-sm rounded-pill px-3 shadow-sm" onClick={handleWhatsAppShare}>
            <i className="bi bi-whatsapp me-1"></i> व्हॉट्सॲपवर पाठवा
          </button>
          <button className="btn btn-outline-dark btn-sm rounded-pill px-3 shadow-sm" onClick={handlePrint}>
            <i className="bi bi-printer me-1"></i> Print / PDF Export
          </button>
          <button className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm" onClick={fetchReport}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>
      </div>

      {/* FILTER CONTROLS (WEB ONLY) */}
      <div className="d-print-none card border-0 shadow-sm mb-4 bg-light">
        <div className="card-body py-3">
          <div className="row g-3 align-items-end">
            {type !== 'stock' && (
              <>
                <div className="col-6 col-md-3">
                  <label className="form-label fw-semibold small">शुरुवात तारीख (From Date)</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label fw-semibold small">शेवट तारीख (To Date)</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* CUSTOMER FILTER */}
            {(type === 'sales' || type === 'customer-ledger') && (
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">व्यापारी नांव (Customer Name)</label>
                <select
                  className="form-select form-select-sm fw-bold"
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                >
                  <option value="0">-- सर्व व्यापारी (All Customers) --</option>
                  {customers.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.Name} {c.Address ? `(${c.Address})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* VENDOR FILTER */}
            {(type === 'purchases' || type === 'vendor-ledger') && (
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">विक्रेता नांव (Vendor/Supplier Name)</label>
                <select
                  className="form-select form-select-sm fw-bold"
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                >
                  <option value="0">-- सर्व सप्लायर (All Vendors) --</option>
                  {vendors.map((v) => (
                    <option key={v.Id} value={v.Id}>
                      {v.Name} {v.Address ? `(${v.Address})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* EMPLOYEE FILTER */}
            {type === 'employees' && (
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">खलाशी नांव (Crew Member Name)</label>
                <select
                  className="form-select form-select-sm fw-bold"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <option value="0">-- सर्व खलाशी (All Crew Members) --</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.Emp_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* SPECIAL JASPER 2-COLUMN SIDE-BY-SIDE LEDGER REPORT VIEW (50% BILLS WITH DETAILED ITEMS + 50% PAYMENTS) */}
      {(type === 'customer-ledger' || type === 'vendor-ledger') && ledgerSummary && (
        <div className="card border-dark shadow-sm mb-4">
          <div className="card-header bg-dark text-white text-center py-2 d-print-none">
            <h5 className="fw-bold mb-0">
              {type === 'customer-ledger' ? 'CUSTOMER WISE ACCOUNT REPORT' : 'VENDOR WISE ACCOUNT REPORT'}
            </h5>
            <span className="small opacity-75">
              {type === 'customer-ledger'
                ? `Customer: ${activeCustomer?.Name || 'All Customers'}`
                : `Vendor: ${activeVendor?.Name || 'All Vendors'}`}
              {' | '}From Date: {startDate} To Date: {endDate}
            </span>
          </div>

          <div className="card-body p-2 p-md-3">
            {/* OPENING BALANCE BANNER */}
            <div className="alert alert-warning border border-warning border-opacity-50 shadow-sm rounded-3 mb-3 d-flex justify-content-between align-items-center py-2 px-3">
              <div>
                <i className="bi bi-clock-history me-2 text-dark fs-5"></i>
                <span className="fw-bold text-dark fs-6">आरंभीची बाकी (Opening Balance prior to {startDate}):</span>
              </div>
              <span className="fw-extrabold text-danger fs-5">
                ₹{(ledgerSummary.openingBalance || 0).toLocaleString('en-IN')}
              </span>
            </div>

            <div className="row g-2">
              {/* LEFT HALF SECTION (50% WIDTH): BILLS WITH DETAILED LINE ITEMS */}
              <div className="col-6 col-md-6">
                <div className="card border border-dark h-100">
                  <div className="card-header bg-primary text-white py-1 py-md-2 fw-bold text-center small">
                    <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                    {type === 'customer-ledger' ? 'विक्री बील (Sales Invoices)' : 'खरेदी बील (Purchase Bills)'}
                  </div>
                  <div className="card-body p-1 p-md-2">
                    {type === 'customer-ledger' ? (
                      ledgerSummary.sales?.length === 0 ? (
                        <p className="text-center text-muted small py-3">No sales bills recorded.</p>
                      ) : (
                        ledgerSummary.sales?.map((s, idx) => (
                          <div key={idx} className="border border-secondary rounded p-2 mb-2 bg-white">
                            <div className="d-flex justify-content-between align-items-center fw-bold border-bottom pb-1 mb-1 small">
                              <span>बील #{s.saleNo} ({s.saleDate})</span>
                              <span className="text-primary">₹{(s.GrandTotal || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {/* DETAILED LINE ITEMS: Product Name, Qty, Rate, Total */}
                            {s.lineItems && s.lineItems.length > 0 && (
                              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.75rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th>सामान नांव (Product)</th>
                                    <th className="text-center">नग (Qty)</th>
                                    <th className="text-center">दर (Rate)</th>
                                    <th className="text-end">एकूण (Total)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.lineItems.map((li, lIdx) => (
                                    <tr key={lIdx}>
                                      <td>{li.productName || 'Fish Item'}</td>
                                      <td className="text-center">{li.net_qty || li.Qty || 1}</td>
                                      <td className="text-center">₹{li.Rate || 0}</td>
                                      <td className="text-end fw-bold">₹{(li.total || 0).toLocaleString('en-IN')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))
                      )
                    ) : (
                      ledgerSummary.purchases?.length === 0 ? (
                        <p className="text-center text-muted small py-3">No purchase bills recorded.</p>
                      ) : (
                        ledgerSummary.purchases?.map((p, idx) => (
                          <div key={idx} className="border border-secondary rounded p-2 mb-2 bg-white">
                            <div className="d-flex justify-content-between align-items-center fw-bold border-bottom pb-1 mb-1 small">
                              <span>बील #{p.purchaseNo || p.InvoiceNo} ({p.purchaseDate})</span>
                              <span className="text-primary">₹{(p.GranadTotal || p.GrandTotal || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {/* DETAILED LINE ITEMS: Product Name, Qty, Rate, Total */}
                            {p.lineItems && p.lineItems.length > 0 && (
                              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.75rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th>सामान नांव (Product)</th>
                                    <th className="text-center">नग (Qty)</th>
                                    <th className="text-center">दर (Rate)</th>
                                    <th className="text-end">एकूण (Total)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.lineItems.map((li, lIdx) => (
                                    <tr key={lIdx}>
                                      <td>{li.productName || 'Item'}</td>
                                      <td className="text-center">{li.Qty || 1}</td>
                                      <td className="text-center">₹{li.Rate || 0}</td>
                                      <td className="text-end fw-bold">₹{(li.total || 0).toLocaleString('en-IN')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))
                      )
                    )}
                  </div>
                  <div className="card-footer bg-primary bg-opacity-10 py-1 py-md-2 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-dark">Total Period Bills :</span>
                    <span className="fw-bold text-primary fs-6">
                      ₹{type === 'customer-ledger'
                        ? (ledgerSummary.totalInvoiced || 0).toLocaleString('en-IN')
                        : (ledgerSummary.totalPurchased || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT HALF SECTION (50% WIDTH): PAYMENTS */}
              <div className="col-6 col-md-6">
                <div className="card border border-dark h-100">
                  <div className="card-header bg-success text-white py-1 py-md-2 fw-bold text-center small">
                    <i className="bi bi-cash-stack me-1"></i>
                    {type === 'customer-ledger' ? 'जमा पावत्या (Payment Receipts)' : 'पेमेंट व्हाउचर (Payment Vouchers)'}
                  </div>
                  <div className="card-body p-1 p-md-2">
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead className="table-light">
                        <tr>
                          <th>तारीख (Date)</th>
                          <th>रिमार्क / Voucher</th>
                          <th className="text-end">जमा Paid (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {type === 'customer-ledger' ? (
                          ledgerSummary.receipts?.length === 0 ? (
                            <tr><td colSpan="3" className="text-center py-3 text-muted">No payment receipts recorded.</td></tr>
                          ) : (
                            ledgerSummary.receipts?.map((r, idx) => (
                              <tr key={idx}>
                                <td>{r.PayDate}</td>
                                <td>{r.Remark || 'Payment Received'}</td>
                                <td className="text-end fw-bold text-success">₹{(r.PaidAmt || 0).toLocaleString('en-IN')}</td>
                              </tr>
                            ))
                          )
                        ) : (
                          ledgerSummary.vouchers?.length === 0 ? (
                            <tr><td colSpan="3" className="text-center py-3 text-muted">No payment vouchers recorded.</td></tr>
                          ) : (
                            ledgerSummary.vouchers?.map((v, idx) => (
                              <tr key={idx}>
                                <td>{v.PayDate}</td>
                                <td>{v.Remark || 'Vendor Payout'}</td>
                                <td className="text-end fw-bold text-success">₹{(v.PaidAmt || 0).toLocaleString('en-IN')}</td>
                              </tr>
                            ))
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="card-footer bg-success bg-opacity-10 py-1 py-md-2 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-dark">Total Paid Amount :</span>
                    <span className="fw-bold text-success fs-6">
                      ₹{type === 'customer-ledger'
                        ? (ledgerSummary.totalReceipts || 0).toLocaleString('en-IN')
                        : (ledgerSummary.totalVouchers || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* JASPER SUMMARY BAND (DOUBLE BORDER FOOTER WITH OPENING BALANCE) */}
          <div className="card-footer bg-light p-2 p-md-3 border-top border-dark border-3">
            <div className="row g-2 justify-content-end text-end">
              <div className="col-12 col-md-7 border-start border-3 border-dark ps-3">
                <div className="d-flex justify-content-between mb-1 fs-6">
                  <span className="fw-bold">Opening Balance (आरंभीची बाकी) :</span>
                  <span className="fw-bold text-dark">
                    ₹{(ledgerSummary.openingBalance || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-1 fs-6">
                  <span className="fw-bold">Period Total Bills (कालावधी बील) :</span>
                  <span className="fw-bold text-primary">
                    ₹{type === 'customer-ledger'
                      ? (ledgerSummary.totalInvoiced || 0).toLocaleString('en-IN')
                      : (ledgerSummary.totalPurchased || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-1 fs-6">
                  <span className="fw-bold">Period Total Paid (कालावधी जमा) :</span>
                  <span className="fw-bold text-success">
                    ₹{type === 'customer-ledger'
                      ? (ledgerSummary.totalReceipts || 0).toLocaleString('en-IN')
                      : (ledgerSummary.totalVouchers || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="d-flex justify-content-between pt-2 border-top border-dark border-2 fs-5">
                  <span className="fw-extrabold text-dark">Net Closing Balance (अंतिम बाकी):</span>
                  <span className="fw-extrabold text-danger">
                    ₹{type === 'customer-ledger'
                      ? (ledgerSummary.closingBalance || ledgerSummary.balanceOutstanding || 0).toLocaleString('en-IN')
                      : (ledgerSummary.closingBalance || ledgerSummary.balancePayable || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STANDARD JASPER REPORT SUMMARY TILES FOR OTHER REPORT TYPES */}
      {summary && type !== 'customer-ledger' && type !== 'vendor-ledger' && (
        <div className="row g-3 mb-4">
          {summary.totalAmount !== undefined && (
            <div className="col-12 col-md-4">
              <div className="card border-0 bg-primary bg-gradient text-white shadow-sm p-3">
                <span className="small text-uppercase fw-bold opacity-75">एकूण व्यवहार (Total Amount)</span>
                <h3 className="fw-bold mb-0">₹{summary.totalAmount.toLocaleString('en-IN')}</h3>
              </div>
            </div>
          )}
          {summary.totalEarned !== undefined && (
            <div className="col-12 col-md-4">
              <div className="card border-0 bg-info bg-gradient text-dark shadow-sm p-3">
                <span className="small text-uppercase fw-bold">खलाशी वाटा (Total Crew Earned)</span>
                <h3 className="fw-bold mb-0">₹{summary.totalEarned.toLocaleString('en-IN')}</h3>
              </div>
            </div>
          )}
          {summary.totalPaid !== undefined && (
            <div className="col-12 col-md-4">
              <div className="card border-0 bg-success bg-gradient text-white shadow-sm p-3">
                <span className="small text-uppercase fw-bold opacity-75">दिलेली उचल (Total Salary Paid)</span>
                <h3 className="fw-bold mb-0">₹{summary.totalPaid.toLocaleString('en-IN')}</h3>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STANDARD REPORT TABLE FOR SALES / PURCHASES / EMPLOYEES / ACCOUNTS / STOCK */}
      {type !== 'customer-ledger' && type !== 'vendor-ledger' && (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 border">
              <thead className="table-dark">
                {type === 'sales' && (
                  <tr>
                    <th>बील #</th>
                    <th>तारीख</th>
                    <th>व्यापारी (Customer)</th>
                    <th className="text-end">एकूण बील (Grand Total)</th>
                    <th className="text-center d-print-none">बील तपशील (Items Detail)</th>
                  </tr>
                )}
                {type === 'purchases' && (
                  <tr>
                    <th>बील #</th>
                    <th>तारीख</th>
                    <th>सप्लायर (Supplier)</th>
                    <th className="text-end">एकूण बील (Grand Total)</th>
                    <th className="text-center d-print-none">बील तपशील (Items Detail)</th>
                  </tr>
                )}
                {type === 'employees' && (
                  <tr>
                    <th>तारीख</th>
                    <th>खलाशी (Crew Member)</th>
                    <th>पलट #</th>
                    <th>एकूण वाटा (Earned)</th>
                    <th>दिलेली रक्कम (Paid)</th>
                    <th>शेरा (Remark)</th>
                  </tr>
                )}
                {type === 'accounts' && (
                  <tr>
                    <th>पलट क्र #</th>
                    <th>तारीख</th>
                    <th>फर्म (Firm)</th>
                    <th>एकूण विक्री (Sale)</th>
                    <th>पलट खर्च (Expenses)</th>
                    <th>५०% वाटा (50%)</th>
                    <th>प्रत्येकी वाटा (Per Crew)</th>
                  </tr>
                )}
                {type === 'stock' && (
                  <tr>
                    <th>#</th>
                    <th>माशाचे नांव (Product Name)</th>
                    <th>युनिट (Unit)</th>
                    <th>खरेदी दर (P.Rate)</th>
                    <th>विक्री दर (S.Rate)</th>
                    <th>सध्याचा साठा (Current Stock)</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2"></span> रिपोर्ट लोड होत आहे...
                    </td>
                  </tr>
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      निवडलेल्या कालावधीत कोणतीही नोंद आढळली नाही (No data available).
                    </td>
                  </tr>
                ) : (
                  reportData.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <tr>
                        {type === 'sales' && (
                          <>
                            <td className="fw-bold">#{row.saleNo}</td>
                            <td>{row.saleDate}</td>
                            <td className="fw-semibold text-primary">{row.customerName || 'नगद (Cash)'}</td>
                            <td className="text-end fw-bold text-success">₹{(row.GrandTotal || 0).toLocaleString('en-IN')}</td>
                            <td className="text-center d-print-none">
                              <button
                                className="btn btn-outline-primary btn-sm py-0 px-2 rounded-pill"
                                onClick={() => toggleRowDetail(row.Id)}
                              >
                                <i className={`bi bi-chevron-${expandedRow === row.Id ? 'up' : 'down'} me-1`}></i>
                                {expandedRow === row.Id ? 'लपवा' : 'आयटम पहा'}
                              </button>
                            </td>
                          </>
                        )}
                        {type === 'purchases' && (
                          <>
                            <td className="fw-bold">#{row.purchaseNo || row.Id}</td>
                            <td>{row.purchaseDate}</td>
                            <td className="fw-semibold text-primary">{row.vendorName || 'Supplier'}</td>
                            <td className="text-end fw-bold text-danger">₹{(row.GranadTotal || 0).toLocaleString('en-IN')}</td>
                            <td className="text-center d-print-none">
                              <button
                                className="btn btn-outline-primary btn-sm py-0 px-2 rounded-pill"
                                onClick={() => toggleRowDetail(row.Id)}
                              >
                                <i className={`bi bi-chevron-${expandedRow === row.Id ? 'up' : 'down'} me-1`}></i>
                                {expandedRow === row.Id ? 'लपवा' : 'आयटम पहा'}
                              </button>
                            </td>
                          </>
                        )}
                        {type === 'employees' && (
                          <>
                            <td>{row.payDate}</td>
                            <td className="fw-semibold text-primary">{row.Emp_name}</td>
                            <td>{row.boat_no ? `पलट #${row.boat_no}` : '-'}</td>
                            <td className="fw-bold text-success">₹{(row.totalAmt || 0).toLocaleString('en-IN')}</td>
                            <td className="fw-bold text-primary">₹{(row.PaidAmt || 0).toLocaleString('en-IN')}</td>
                            <td>{row.Remark || '-'}</td>
                          </>
                        )}
                        {type === 'accounts' && (
                          <>
                            <td className="fw-bold">पलट #{row.boat_no}</td>
                            <td>{row.boat_date}</td>
                            <td>{row.firmName}</td>
                            <td className="fw-bold text-success">₹{(row.total_sale || 0).toLocaleString('en-IN')}</td>
                            <td className="text-danger">₹{(row.boat_total || 0).toLocaleString('en-IN')}</td>
                            <td className="fw-bold text-primary">₹{(row.fifty_percent || 0).toLocaleString('en-IN')}</td>
                            <td>
                              <span className="badge bg-info text-dark">
                                ₹{parseFloat(row.per_employee || 0).toFixed(2)}
                              </span>
                            </td>
                          </>
                        )}
                        {type === 'stock' && (
                          <>
                            <td>{idx + 1}</td>
                            <td className="fw-semibold">{row.productName}</td>
                            <td><span className="badge bg-secondary">{row.unit}</span></td>
                            <td>₹{row.PRate || 0}</td>
                            <td>₹{row.SRate || 0}</td>
                            <td className="fw-bold text-primary">{row.currentQty || 0}</td>
                          </>
                        )}
                      </tr>

                      {/* BILL LINE ITEMS SUMMARY DETAILS (Product Name, Qty, Rate, Total) */}
                      {(expandedRow === row.Id || true) && row.lineItems && row.lineItems.length > 0 && (
                        <tr className={`${expandedRow === row.Id ? '' : 'd-none d-print-table-row'} bg-light`}>
                          <td colSpan="5" className="p-3">
                            <div className="card border border-secondary border-opacity-25 rounded-3 p-2 bg-white">
                              <h6 className="fw-bold text-dark mb-2 small">
                                <i className="bi bi-list-check me-2 text-primary"></i>बील आयटम समरी (Bill Items Summary - Bill #{row.saleNo || row.purchaseNo || row.Id})
                              </h6>
                              <table className="table table-sm table-bordered align-middle mb-0 small">
                                <thead className="table-light">
                                  <tr>
                                    <th>सामान नांव (Product Name)</th>
                                    <th className="text-center">प्रमाण / नग (Qty)</th>
                                    <th className="text-center">दर (Rate ₹)</th>
                                    <th className="text-end">एकूण (Total ₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.lineItems.map((li, lIdx) => (
                                    <tr key={lIdx}>
                                      <td className="fw-semibold">{li.productName || 'Fish Item'}</td>
                                      <td className="text-center fw-bold">{li.net_qty || li.Qty || 1}</td>
                                      <td className="text-center">₹{li.Rate || 0}</td>
                                      <td className="text-end fw-bold text-success">₹{(li.total || 0).toLocaleString('en-IN')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
